import { useEffect, useState } from 'react';
import { api } from './api.js';
import { BRANDING_CHANGED } from './settingsEvents.js';

/**
 * ehr#492: Data & settings → Branding & labels saves a product name and word overrides
 * ({ Task: 'To-do' }), but nothing read them back. The sidebar, page titles and buttons now do.
 */
export interface Branding {
  displayName: string;
  labels: Record<string, string>;
}
const EMPTY: Branding = { displayName: '', labels: {} };

const plural = (w: string) =>
  /s$/i.test(w) ? w : /[^aeiou]y$/i.test(w) ? `${w.slice(0, -1)}ies` : `${w}s`;
const sameCase = (model: string, w: string) =>
  model === model.toUpperCase() && model !== model.toLowerCase()
    ? w.toUpperCase()
    : model[0] === model[0]!.toUpperCase()
      ? w[0]!.toUpperCase() + w.slice(1)
      : w[0]!.toLowerCase() + w.slice(1);
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Swaps each overridden word (whole word, singular or plural) for its label, keeping its capitals. */
export function relabel(text: string, labels: Record<string, string>): string {
  let out = text;
  for (const [from, to] of Object.entries(labels)) {
    const f = from.trim();
    const t = to.trim();
    if (!f || !t || f.toLowerCase() === t.toLowerCase()) continue;
    const re = new RegExp(`\\b(${escapeRe(plural(f))}|${escapeRe(f)})\\b`, 'gi');
    out = out.replace(re, (m) => sameCase(m, m.toLowerCase() === f.toLowerCase() ? t : plural(t)));
  }
  return out;
}

let cache: Branding | null = null;
let inflight: Promise<Branding> | null = null;
function load(): Promise<Branding> {
  inflight ??= api
    .get<Branding>('/practice/branding')
    .then((b) => (cache = { displayName: b?.displayName ?? '', labels: b?.labels ?? {} }))
    // not cached: before sign-in this answers 401, and the next mount (or key) asks again
    .catch(() => EMPTY)
    .finally(() => (inflight = null));
  return inflight;
}

/**
 * The practice's product name and word overrides; re-read after Data & settings saves them.
 * `key` (e.g. the signed-in user's id) asks again when it changes.
 */
export function useBranding(key?: string | null): Branding {
  const [b, setB] = useState<Branding>(cache ?? EMPTY);
  useEffect(() => {
    let live = true;
    const refresh = () => void load().then((v) => live && setB(v));
    if (!cache) refresh();
    else setB(cache);
    const onChange = () => {
      cache = null;
      refresh();
    };
    window.addEventListener(BRANDING_CHANGED, onChange);
    return () => {
      live = false;
      window.removeEventListener(BRANDING_CHANGED, onChange);
    };
  }, [key]);
  return b;
}
